import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import { createPortal }          from 'react-dom';
import { useEffect }             from 'react';
import { useState }              from 'react';
import { observer }              from 'mobx-react-lite';
import { ScissorOutlined }       from '@ant-design/icons';
import type { Shape }            from '@tokamap/core';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import Kbd                       from '@/TokamapEditor/ux/Kbd';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { splitShapes }           from '@/TokamapEditor/helpers/splitShape';
import { previewSplit }          from '@/TokamapEditor/helpers/splitShape';

const CutToolBar = observer(() => {
	const tokens = useDesignTokens();
	const instance = currentEditorStore.get;
	const mapAdapter = instance?.mapAdapter;
	const container = mapAdapter?.getContainerElement();
	const [active, setActive] = useState(() => mapAdapter?.isDrawingCut() ?? false);
	const cursor = scissorsCursor({ blade: tokens.text.default, handle: tokens.color.primary, outline: tokens.surface.card });

	useEffect(() => {
		if (!instance) return;

		setActive(instance.mapAdapter.isDrawingCut());

		const targets = (shapeIds: string[]) => shapeIds.length
			? shapeIds.map(id => instance.shapeStore.get(id)).filter((shape): shape is Shape => !!shape)
			: instance.shapeStore.getAll();

		return instance.mapAdapter.on(event => {
			if (event.type === 'cut:tool-changed') return setActive(event.active);

			if (event.type === 'cut:draft-changed') {
				return instance.mapAdapter.setCutPreview(previewSplit(targets(event.shapeIds), event.line));
			}

			if (event.type === 'shape:cut-requested') {
				void splitShapes(targets(event.shapeIds).map(shape => shape.id), event.line);
			}
		});
	}, [instance]);

	useEffect(() => {
		if (!active || !container) return;

		container.classList.add(ACTIVE_CLASS);
		container.style.setProperty(CURSOR_PROPERTY, cursor);

		return () => {
			container.classList.remove(ACTIVE_CLASS);
			container.style.removeProperty(CURSOR_PROPERTY);
		};
	}, [active, container, cursor]);

	if (!active || !mapAdapter || !container) return null;

	return createPortal(
		<View abs absB={16} absL={0} w="100%" flex center zIndex={10} style={{ pointerEvents: 'none', userSelect: 'none' }}>
			<View
				row
				centerV
				gap="md"
				pl={16}
				pr={8}
				py={8}
				br={999}
				bg={tokens.surface.elevated}
				size={13}
				style={{ boxShadow: tokens.shadow.elevated, pointerEvents: 'auto' }}
			>
				<View row centerV gap="xs" bold>
					<ScissorOutlined style={{ fontSize: 16, color: tokens.color.primary }} />
					Mode découpe
				</View>

				<View row centerV gap="sm" size={12} color={tokens.text.secondary}>
					<View row centerV gap="xs"><Kbd>Clic</Kbd>poser un point de la coupe</View>
					<View row centerV gap="xs"><Kbd>Entrée</Kbd>couper</View>
					<View row centerV gap="xs"><Kbd>Maj</Kbd>magnétisme</View>
					<View row centerV gap="xs"><Kbd>Alt</Kbd>traits droits</View>
				</View>

				<Button onClick={() => mapAdapter.stopDrawingCut()}>Terminer</Button>
			</View>
		</View>,
		container,
	);
});

export default CutToolBar;

const ACTIVE_CLASS = 'cut-tool-active';

const CURSOR_PROPERTY = '--cut-tool-cursor';

/** Ciseaux aux couleurs du thème, lames vers le haut à gauche : le point actif est leur pointe. */
function scissorsCursor(colors: { blade: string; handle: string; outline: string }): string {
	const blades = '<path d="M3 9L22 19M9 3L19 22"/>';
	const handles = '<circle cx="25" cy="21" r="3.5"/><circle cx="21" cy="25" r="3.5"/>';
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" stroke-linecap="round">`
		+ `<g stroke="${colors.outline}" stroke-width="5">${blades}${handles}</g>`
		+ `<g stroke="${colors.blade}" stroke-width="2.5">${blades}</g>`
		+ `<g stroke="${colors.handle}" stroke-width="2.5">${handles}</g>`
		+ `</svg>`;

	return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 6 6, crosshair`;
}
